import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import Select from '../Select/Select';
import AscIcon from '@/assets/ascending.svg?react';
import DescIcon from '@/assets/descending.svg?react';
import { ga } from '@/lib/analytics';

export type SortOrder = 'asc' | 'desc';

// "Sort By" field select with an optional direction toggle - the toggle
// shows only when both `sortOrder` and `onSortOrderChange` are passed.
const SortControl = ({
  sortBy,
  onSortByChange,
  options,
  sortOrder,
  onSortOrderChange,
  defaultSortBy,
  defaultSortOrder = 'desc',
  className,
}: {
  sortBy: string;
  onSortByChange: (s: string) => void;
  options: {
    id: number;
    label: ReactNode;
    value: string;
  }[];
  sortOrder?: SortOrder;
  onSortOrderChange?: (v: SortOrder) => void;
  defaultSortBy?: string;
  defaultSortOrder?: SortOrder;
  className?: string;
}) => {
  const resolvedDefaultSortBy = defaultSortBy ?? options[0]?.value;
  const isDefaultSort =
    sortBy === resolvedDefaultSortBy &&
    (sortOrder == null || sortOrder === defaultSortOrder);

  const applySort = (
    nextSortBy: string,
    nextSortOrder: SortOrder,
  ) => {
    if (nextSortBy !== sortBy) {
      onSortByChange(nextSortBy);
    }
    if (sortOrder != null && nextSortOrder !== sortOrder) {
      onSortOrderChange?.(nextSortOrder);
    }
    ga.trackSortApply({
      sort_by: nextSortBy,
      sort_order: sortOrder != null ? nextSortOrder : undefined,
    });
  };

  // `null` arrives from the clear (X) button and from re-clicking the
  // already selected option — both mean "back to the default sort".
  // Picking a different option resets the direction too, so every sort
  // starts from the same baseline.
  const handleSortChange = (value: string | number | null) => {
    applySort(
      value == null ? resolvedDefaultSortBy : String(value),
      defaultSortOrder,
    );
  };

  const handleSortOrderChange = (value: SortOrder) => {
    applySort(sortBy, value);
  };

  return (
    <SelectWrapper className={className}>
      <Label>
        <FormattedMessage id="sortBy" defaultMessage="Sort By" />
      </Label>
      <Select
        options={options}
        onChange={handleSortChange}
        value={sortBy}
        variant="text"
        showClear={!isDefaultSort}
        aria-label="Sort resources"
      />
      {sortOrder != null && onSortOrderChange && (
        <SortOrderSwitch
          sortOrder={sortOrder}
          onChange={handleSortOrderChange}
        />
      )}
    </SelectWrapper>
  );
};

const SelectWrapper = styled('div')(() => ({
  display: 'flex',
  flexGrow: 0,
  alignItems: 'center',
}));

const Label = styled('div')(({ theme }) => ({
  color: theme.palette.grey400,
  textWrap: 'nowrap',
  marginRight: theme.spacing(2),
  fontSize: '14px',
  lineHeight: '20px',
}));

const SortOrderSwitchRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: theme.radii.sm,
  border: `1px solid ${theme.palette.primary.main}`,
  height: 40,

  '> div:first-of-type': {
    borderRight: `1px solid ${theme.palette.primary.main}`,
  },
}));

const SortOrderButton = styled('div')<{
  isSelected: boolean;
  isLeft: boolean;
}>(({ theme, isSelected, isLeft }) => ({
  width: 48,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: isSelected ? theme.palette.red50 : theme.palette.white,
  borderTopLeftRadius: isLeft ? theme.radii.sm : 0,
  borderBottomLeftRadius: isLeft ? theme.radii.sm : 0,
  borderTopRightRadius: isLeft ? 0 : theme.radii.sm,
  borderBottomRightRadius: isLeft ? 0 : theme.radii.sm,
  cursor: 'pointer',
  transition: 'background 0.2s ease-in-out',
  path: {
    stroke: isSelected
      ? theme.palette.primary.main
      : theme.palette.grey600,
  },
}));

const SortOrderSwitch = ({
  sortOrder,
  onChange,
}: {
  sortOrder: SortOrder;
  onChange: (v: SortOrder) => void;
}) => {
  return (
    <SortOrderSwitchRoot aria-label="Sort order switch">
      <SortOrderButton
        isSelected={sortOrder === 'asc'}
        isLeft
        onClick={() => onChange('asc')}
        aria-label="Ascending order"
      >
        <AscIcon />
      </SortOrderButton>
      <SortOrderButton
        isSelected={sortOrder === 'desc'}
        isLeft={false}
        onClick={() => onChange('desc')}
        aria-label="Descending order"
      >
        <DescIcon />
      </SortOrderButton>
    </SortOrderSwitchRoot>
  );
};

export default SortControl;
